import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import * as LanternApi from 'src/utilities/LanternApiClient';
import { useSnackbar } from 'notistack';
import { useRole } from './RoleContext';
import useAuth from 'src/hooks/useAuth';

const SelectedClientsContext = createContext();

export const useSelectedClients = () => {
  const ctx = useContext(SelectedClientsContext);
  if (!ctx) throw new Error('useSelectedClients must be used within SelectedClientsProvider');
  return ctx;
};

const storageKey = (role) => `lantern:selectedClients:${role || 'All'}`;

export const SelectedClientsProvider = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { selectedRole } = useRole();
  const { user } = useAuth();

  const [availableClients, setAvailableClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);

  const selectedClientIds = useMemo(() => selectedClients.map((c) => c.id), [selectedClients]);

  const refreshAvailableClients = useCallback(async () => {
    setLoading(true);
    try {
      // Prefer parent clients (authorized); fallback to child clients if unavailable
      let resp = null;
      if (user?.id) {
        resp = await LanternApi.getParentClientsByUser(user.id);
      }
      if (!resp || !resp.success) {
        resp = await LanternApi.getChildClientsByUser();
      }
      if (resp?.success) {
        // Parent-client shape or child-client shape; normalize to id + name
        const items = Array.isArray(resp.body) ? resp.body : [];

        // If "All Clients" is present, hydrate full parent client list
        const hasAllClients = items.some((c) => (c.name ?? c.parentName ?? c.Name)?.toLowerCase() === 'all clients');

        let clients = [];
        if (hasAllClients) {
          const allParentsResp = await LanternApi.getParentClients();
          if (allParentsResp?.success) {
            const records = Array.isArray(allParentsResp.body?.records) ? allParentsResp.body.records : [];
            clients = records.map((p) => ({ id: p.id ?? p.Id, name: p.name ?? p.Name })).filter((x) => x.id && x.name);
          } else {
            // Fallback to the original items if global fetch fails
            clients = items
              .map((c) => ({ id: c.id ?? c.parentClientId ?? c.Id, name: c.name ?? c.parentName ?? c.Name }))
              .filter((x) => x.id && x.name);
          }
        } else {
          clients = items
            .map((c) => ({ id: c.id ?? c.parentClientId ?? c.Id, name: c.name ?? c.parentName ?? c.Name }))
            .filter((x) => x.id && x.name);
        }

        setAvailableClients(clients);

        // Rehydrate persisted selection filtered to currently available clients
        const persisted = JSON.parse(localStorage.getItem(storageKey(selectedRole)) || '[]');
        if (Array.isArray(persisted) && persisted.length) {
          const nextSelected = clients.filter((c) => persisted.includes(c.id));
          setSelectedClients(nextSelected);
        }
      } else {
        enqueueSnackbar('Failed to load your client list.', { variant: 'error' });
      }
    } catch (e) {
      enqueueSnackbar('An unexpected error occurred loading clients.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, selectedRole, user?.id]);

  // Load available clients on mount and when role changes
  useEffect(() => {
    refreshAvailableClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole]);

  // Persist selection per role
  useEffect(() => {
    localStorage.setItem(storageKey(selectedRole), JSON.stringify(selectedClientIds));
  }, [selectedClientIds, selectedRole]);

  const clearSelected = useCallback(() => setSelectedClients([]), []);

  const setSelectedByIds = useCallback(
    (ids) => {
      const next = availableClients.filter((c) => ids.includes(c.id));
      setSelectedClients(next);
    },
    [availableClients],
  );

  const value = {
    // Data
    availableClients,
    selectedClients,
    selectedClientIds,
    loadingAvailableClients: loading,

    // Actions
    refreshAvailableClients,
    setSelectedClients,
    setSelectedByIds,
    clearSelected,
  };

  return <SelectedClientsContext.Provider value={value}>{children}</SelectedClientsContext.Provider>;
};

export default SelectedClientsContext;
